import { faCheck, faEdit, faEye, faEyeSlash, faPlus, faRedo, faSave, faShieldAlt, faSpinner, faTimes, faUniversity } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Card, Grid, Stack, Text, TextInput } from "@mantine/core";
import React, { useState } from "react";
import ABAApi from "../../../data/api/abaApi";
import { PersonBankDetails } from "../../../types/aba";
import { Privilege } from "../../../types/persons/privilege";
import { PersonBankDetailsForm, SensitivityWarning } from "../../aba";
import { CenteredLoader } from "../../common/loader";
import { PersonsTabProps } from './PersonEditor';

interface BankDetailsTabProps extends PersonsTabProps {
  /** Self privilege level */
  selfPrivilege: Privilege;
  /** Whether the form is active/editable */
  active: boolean;
  /** Callback to refresh person data */
  refreshPerson: () => void;
  /** Available associations */
}

/**
 * Bank details tab for person editor - allows admin to view/edit bank details
 * Implements request-to-view pattern for sensitive data
 */
const BankDetailsTab: React.FunctionComponent<BankDetailsTabProps> = ({
  person,
  selfPrivilege,
  active,
  refreshPerson,
  associations = {},
  associationIdContext
}) => {
  const [bankDetails, setBankDetails] = useState<PersonBankDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRequestedView, setHasRequestedView] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<PersonBankDetails>>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [showAddModal, setShowAddModal] = useState(false);

  const canEdit = Privilege.canManage(selfPrivilege);

  // Get the first association ID for this person
  const getAssociationId = (): string => {
    if (associationIdContext) return associationIdContext;
    if (person.associations && person.associations.length > 0) {
      return person.associations[0].associationId;
    }
    // Fallback to first association in props if person doesn't have associations loaded
    const associationIds = Object.keys(associations);
    return associationIds.length > 0 ? associationIds[0] : '';
  };

  // Load bank details when requested
  const loadBankDetails = async () => {
    if (!person._id) return;

    setLoading(true);
    setError(null);

    try {
      const associationId = getAssociationId();
      const details = await ABAApi.getPersonBankDetails(person._id, associationId);
      setBankDetails(details);
      setEditFormData({
        bsb: details.bsb,
        accountNumber: details.accountNumber,
        accountName: details.accountName,
      });
    } catch (err) {
      if (err instanceof Error && err.message.includes("404")) {
        // No bank details found
        setBankDetails(null);
        setEditFormData({
          bsb: "",
          accountNumber: "",
          accountName: "",
        });
      } else {
        setError(err instanceof Error ? err.message : "Failed to load bank details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestView = () => {
    setHasRequestedView(true);
    loadBankDetails();
  };

  const handleEdit = () => {
    setIsEditing(true);
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setValidationErrors({});
    // Reset form data
    if (bankDetails) {
      setEditFormData({
        bsb: bankDetails.bsb,
        accountNumber: bankDetails.accountNumber,
        accountName: bankDetails.accountName,
      });
    }
  };

  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case "bsb":
        if (!value) return "BSB is required";
        if (!/^\d{6}$|^\d{3}-\d{3}$/.test(value)) {
          return "BSB must be 6 digits (e.g., 062000 or 062-000)";
        }
        return null;

      case "accountNumber":
        if (!value) return "Account number is required";
        if (!/^\d{1,9}$/.test(value)) {
          return "Account number must be up to 9 digits";
        }
        return null;

      case "accountName":
        if (!value) return "Account name is required";
        if (value.length > 32) {
          return "Account name must be 32 characters or less";
        }
        if (!/^[A-Z0-9\s\-\.&']+$/i.test(value)) {
          return "Only letters, numbers, spaces, -, ., &, ' allowed";
        }
        return null;

      default:
        return null;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    const error = validateField(name, value);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    ["bsb", "accountNumber", "accountName"].forEach(field => {
      const value = editFormData[field] || "";
      const error = validateField(field, value);
      if (error) {
        errors[field] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!person._id) return;

    setSaving(true);
    setError(null);

    try {
      const associationId = getAssociationId();
      const updatedDetails = await ABAApi.updatePersonBankDetails(person._id, associationId, editFormData);
      setBankDetails(updatedDetails);
      setIsEditing(false);
      refreshPerson(); // Refresh the person data in parent
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save bank details");
    } finally {
      setSaving(false);
    }
  };

  const handleValidateBankDetails = async () => {
    if (!person._id) return;

    setLoading(true);
    setError(null);

    try {
      const associationId = getAssociationId();
      if (!bankDetails) {
        setError("No bank details to validate");
        return;
      }

      const result = await ABAApi.validatePersonBankDetails(associationId, {
        bsb: bankDetails.bsb,
        accountNumber: bankDetails.accountNumber,
        accountName: bankDetails.accountName,
      });

      if (result.isValid) {
        // Reload bank details to get updated validation status
        await loadBankDetails();
      } else {
        setError(`Bank details are invalid: ${result.errors?.join(", ")}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to validate bank details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddBankDetails = () => {
    setShowAddModal(true);
  };

  const handleBankDetailsSaved = (details: PersonBankDetails) => {
    setBankDetails(details);
    setShowAddModal(false);
    setError(null);
    refreshPerson(); // Refresh the person data in parent
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  if (!canEdit) {
    return (
      <Alert color="yellow">
        <p>You don't have permission to view or edit bank details.</p>
      </Alert>
    );
  }

  if (!hasRequestedView) {
    return (
      <div>
        <SensitivityWarning
          message="Bank details contain sensitive financial information. Click below to view this person's bank details."
        />

        <Card>
          <Card.Section withBorder p="md">
            <h6 className="mb-0">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Sensitive Information Access
            </h6>
          </Card.Section>
          <div style={{ padding: "1rem" }} className="text-center">
            <FontAwesomeIcon icon={faEyeSlash} size="3x" className="text-muted mb-3" />
            <h5>Bank Details Hidden</h5>
            <p className="text-muted">
              This person's bank details are hidden for security. Click the button below
              to reveal and edit their banking information.
            </p>
            <Button color="blue" onClick={handleRequestView}>
              <FontAwesomeIcon icon={faEye} className="me-2" />
              View Bank Details
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <div>
      {/* Bank Details Display/Edit */}
      <Card className="mb-4">
        <Card.Section withBorder p="md" className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faUniversity} className="me-2" />
            Bank Details for {person.preferredName || person.firstName}
          </h6>
          {!isEditing && bankDetails && (
            <div>
              {/* <Button
                color="cyan"
                variant="outline"
                size="sm"
                className="me-2"
                onClick={handleValidateBankDetails}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                Validate
              </Button> */}
              <Button color="blue" variant="outline" size="sm" onClick={handleEdit}>
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Edit
              </Button>
            </div>
          )}
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {error ? (
            // Show error message instead of content when there's an error
            <Alert color="red">
              <h6>Error Loading Bank Details</h6>
              <p className="mb-2">{error}</p>
              <Button
                color="red"
                variant="outline"
                size="sm"
                onClick={loadBankDetails}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faRedo} className="me-2" />
                Retry
              </Button>
            </Alert>
          ) : bankDetails ? (
            <div>
              {!isEditing ? (
                // Display mode
                <div>
                  <Grid>
                    <Grid.Col span={6} lg={6} className="mb-3 mb-lg-0">
                      <div className="mb-3">
                        <strong className="d-block mb-1">BSB:</strong>
                        <code>{bankDetails.bsb}</code>
                      </div>

                      <div className="mb-3">
                        <strong className="d-block mb-1">Account Number:</strong>
                        <code>{bankDetails.accountNumber}</code>
                      </div>

                      <div className="mb-3">
                        <strong className="d-block mb-1">Account Name:</strong>
                        <div>{bankDetails.accountName}</div>
                      </div>
                    </Grid.Col>

                    <Grid.Col span={6} lg={6}>
                      <div className="mb-3">
                        <strong className="d-block mb-1">Status:</strong>
                        {bankDetails.isValid ? (
                          <span className="badge bg-success">
                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                            Valid
                          </span>
                        ) : (
                          <span className="badge bg-danger">
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Invalid
                          </span>
                        )}
                      </div>

                      {bankDetails.lastFourDigits && (
                        <div className="mb-3">
                          <strong className="d-block mb-1">Last 4 Digits:</strong>
                          <code>****{bankDetails.lastFourDigits}</code>
                        </div>
                      )}

                      {bankDetails.lastValidated && (
                        <div className="mb-3">
                          <strong className="d-block mb-1">Last Validated:</strong>
                          <div className="text-muted">
                            {new Date(bankDetails.lastValidated).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </Grid.Col>
                  </Grid>
                </div>
              ) : (
                // Edit mode
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={6} lg={6} className="mb-3">
                      <Stack gap="sm">
                        <label className="fw-500" htmlFor="bsb">
                          BSB <span className="text-danger">*</span>
                        </label>
                        <TextInput
                          type="text"
                          name="bsb"
                          id="bsb"
                          value={editFormData.bsb || ""}
                          onChange={handleInputChange}
                          disabled={saving}
                          invalid={!!validationErrors.bsb}
                          placeholder="062000 or 062-000"
                          maxLength={7}
                        />
                        {validationErrors.bsb && (
                          <div className="text-danger small mt-1">
                            {validationErrors.bsb}
                          </div>
                        )}
                      </Stack>
                    </Grid.Col>

                    <Grid.Col span={6} lg={6} className="mb-3">
                      <Stack gap="sm">
                        <label className="fw-500" htmlFor="accountNumber">
                          Account Number <span className="text-danger">*</span>
                        </label>
                        <TextInput
                          type="text"
                          name="accountNumber"
                          id="accountNumber"
                          value={editFormData.accountNumber || ""}
                          onChange={handleInputChange}
                          disabled={saving}
                          invalid={!!validationErrors.accountNumber}
                          placeholder="123456789"
                          maxLength={9}
                        />
                        {validationErrors.accountNumber && (
                          <div className="text-danger small mt-1">
                            {validationErrors.accountNumber}
                          </div>
                        )}
                      </Stack>
                    </Grid.Col>
                  </Grid>

                  <Stack gap="sm" className="mb-4">
                    <label className="fw-500" htmlFor="accountName">
                      Account Name <span className="text-danger">*</span>
                    </label>
                    <TextInput
                      type="text"
                      name="accountName"
                      id="accountName"
                      value={editFormData.accountName || ""}
                      onChange={handleInputChange}
                      disabled={saving}
                      invalid={!!validationErrors.accountName}
                      placeholder="Account holder name"
                      maxLength={32}
                    />
                    {validationErrors.accountName && (
                      <div className="text-danger small mt-1">
                        {validationErrors.accountName}
                      </div>
                    )}
                  </Stack>

                  <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
                    <Button
                      color="gray"
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="order-2 order-sm-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      color="blue"
                      onClick={handleSave}
                      disabled={saving || Object.keys(validationErrors).length > 0}
                      className="order-1 order-sm-2"
                    >
                      {saving ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSave} className="me-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                </Stack>
              )}
            </div>
          ) : (
            // No bank details found
            <div>
              <Alert color="blue">
                <h6>No Bank Details</h6>
                <p className="mb-0">
                  This person doesn't have bank details on file.
                </p>
              </Alert>

              <Button color="blue" onClick={handleAddBankDetails}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Bank Details
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Security Information */}
      <Alert color="blue">
        <h6>
          <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
          Security Information
        </h6>
        <ul className="mb-0">
          <li>Bank details are encrypted and stored securely</li>
          <li>All changes are logged for audit purposes</li>
          <li>Only users with management privileges can view/edit bank details</li>
          <li>Validation ensures bank details are in the correct format</li>
        </ul>
      </Alert>

      {/* Add Bank Details Modal */}
      {showAddModal && (
        <PersonBankDetailsForm
          personId={person._id}
          associationId={getAssociationId()}
          personName={person.preferredName || person.firstName}
          isModal={true}
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSave={handleBankDetailsSaved}
          isSelf={false}
        />
      )}
    </div>
  );
};

export default BankDetailsTab;
